/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * SipJsVoiceTransport.ts — SIP.js implementation of the voice transport contract.
 * It registers the browser operator, accepts the assigned invitation, connects
 * remote media, and tears down SIP resources deterministically. User-facing
 * localization is handled by the hook consuming its stable error codes.
 */

/**
 * E3-S2 T1: WebRTC voice transport using SIP.js — registration, incoming INVITE, accept, BYE.
 * E3-S2 T2: Device selection — preferred input constraints and output device (setSinkId on remote audio).
 */
import {
  UserAgent,
  Registerer,
  Invitation,
  SessionState,
  type UserAgentOptions,
  type UserAgentDelegate,
  type Session,
} from 'sip.js';
import type {
  CallVoiceState,
  CallVoiceConnectParams,
  CallVoiceTransportCallbacks,
  ICallVoiceTransport,
} from './types';

const INVITE_WAIT_MS = 30_000;

export interface SipJsVoiceTransportConfig {
  sipWsUrl: string;
  sipDomain: string;
  /** Optional password for SIP registration (e.g. empty when auth disabled). */
  sipPassword?: string;
}

export class SipJsVoiceTransport implements ICallVoiceTransport {
  private config: SipJsVoiceTransportConfig;
  private callbacks: CallVoiceTransportCallbacks;
  private state: CallVoiceState = 'disconnected';
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private currentInvitation: Invitation | null = null;
  private activeSession: Session | null = null;
  private connectParams: CallVoiceConnectParams | null = null;
  private inviteWaitTimer: ReturnType<typeof setTimeout> | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;

  constructor(config: SipJsVoiceTransportConfig, callbacks: CallVoiceTransportCallbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
  }

  connect(params: CallVoiceConnectParams): void {
    const { calleeId } = params;
    if (this.userAgent?.isConnected() || this.state === 'connecting' || this.state === 'starting') {
      this.callbacks.onError?.('Already connected or connecting');
      return;
    }
    this.connectParams = params;
    this.setState('connecting');

    const uri = UserAgent.makeURI(`sip:${calleeId}@${this.config.sipDomain}`);
    if (!uri) {
      this.setState('disconnected');
      this.callbacks.onError?.('Invalid SIP URI');
      return;
    }

    const transportOptions = { server: this.config.sipWsUrl };
    const delegate: UserAgentDelegate = {
      onInvite: (invitation: Invitation) => this.handleInvite(invitation),
      onDisconnect: (error) => {
        if (error) this.callbacks.onError?.(error.message);
        this.cleanup();
        this.setState('ended');
      },
    };

    const options: Partial<UserAgentOptions> = {
      uri,
      authorizationUsername: calleeId,
      authorizationPassword: this.config.sipPassword ?? '',
      displayName: calleeId,
      transportOptions,
      delegate,
      noAnswerTimeout: 60,
      logLevel: 'error',
      logBuiltinEnabled: false,
      sessionDescriptionHandlerFactoryOptions: {
        constraints: { audio: true, video: false },
      },
    };

    try {
      this.userAgent = new UserAgent(options);
      this.userAgent
        .start()
        .then(() => {
          this.setState('starting');
          this.registerer = new Registerer(this.userAgent!);
          return this.registerer.register({ requestDelegate: {} });
        })
        .then(() => {
          this.clearInviteWaitTimer();
          this.inviteWaitTimer = setTimeout(() => {
            if (this.state === 'connecting' || this.state === 'starting') {
              this.callbacks.onError?.('voice.invite_timeout');
              this.endCall();
            }
          }, INVITE_WAIT_MS);
        })
        .catch((err: Error) => {
          this.callbacks.onError?.(err.message ?? 'SIP start/register failed');
          this.cleanup();
          this.setState('disconnected');
        });
    } catch (err) {
      this.callbacks.onError?.(err instanceof Error ? err.message : 'SIP UserAgent failed');
      this.setState('disconnected');
    }
  }

  private handleInvite(invitation: Invitation): void {
    this.clearInviteWaitTimer();
    if (this.state !== 'connecting' && this.state !== 'starting') {
      invitation.reject().catch(() => {});
      return;
    }
    this.currentInvitation = invitation;

    const stateListener = (): void => {
      if (invitation.state === SessionState.Established) {
        invitation.stateChange.removeListener(stateListener);
        this.currentInvitation = null;
        this.activeSession = invitation;
        this.attachRemoteAudio(invitation);
        this.setState('active');
        this.callbacks.onSessionStarted?.(
          this.connectParams?.callId ?? '',
          this.connectParams?.sessionId ?? ''
        );
      } else if (
        invitation.state === SessionState.Terminated ||
        invitation.state === SessionState.Terminating
      ) {
        invitation.stateChange.removeListener(stateListener);
        this.currentInvitation = null;
        if (this.activeSession === invitation) this.activeSession = null;
        if (this.state === 'active') this.setState('ended');
      }
    };
    invitation.stateChange.addListener(stateListener);

    const inputConstraints = this.connectParams?.preferredInputConstraints;
    const constraints: MediaStreamConstraints = {
      audio: inputConstraints ?? true,
      video: false,
    };
    invitation
      .accept({
        sessionDescriptionHandlerOptions: { constraints },
      })
      .then(() => {
        // Session state will transition to Established; stateListener handles it.
      })
      .catch((err: Error) => {
        this.callbacks.onError?.(err.message ?? 'Accept failed');
        this.currentInvitation = null;
        this.cleanup();
        this.setState('disconnected');
      });
  }

  endCall(): void {
    this.clearInviteWaitTimer();
    if (this.currentInvitation) {
      const inv = this.currentInvitation;
      this.currentInvitation = null;
      inv
        .reject()
        .catch(() => {})
        .finally(() => this.cleanup());
    } else if (this.activeSession) {
      const session = this.activeSession;
      this.activeSession = null;
      session
        .bye()
        .catch(() => {})
        .finally(() => this.cleanup());
    } else {
      this.cleanup();
    }
    this.setState('ended');
  }

  getState(): CallVoiceState {
    return this.state;
  }

  /** WebRTC uses RTP; PCM send is not used. */
  sendPcm16(): void {
    // no-op
  }

  sendChunk(): void {
    // no-op
  }

  private setState(s: CallVoiceState): void {
    this.state = s;
    this.callbacks.onState?.(s);
  }

  /** E3-S2 T2: Attach remote stream to an audio element and apply preferred output device. */
  private attachRemoteAudio(session: Session): void {
    const handler = session.sessionDescriptionHandler as
      { remoteMediaStream?: MediaStream } | undefined;
    const stream = handler?.remoteMediaStream;
    if (!stream) return;
    const el = document.createElement('audio');
    el.autoplay = true;
    el.srcObject = stream;
    const outputId = this.connectParams?.preferredOutputId;
    if (outputId && typeof el.setSinkId === 'function') {
      el.setSinkId(outputId).catch(() => {});
    }
    el.play().catch(() => {});
    this.remoteAudioElement = el;
  }

  private clearInviteWaitTimer(): void {
    if (this.inviteWaitTimer) {
      clearTimeout(this.inviteWaitTimer);
      this.inviteWaitTimer = null;
    }
  }

  private cleanup(): void {
    this.clearInviteWaitTimer();
    this.currentInvitation = null;
    this.activeSession = null;
    this.connectParams = null;
    if (this.remoteAudioElement) {
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement.remove();
      this.remoteAudioElement = null;
    }
    if (this.registerer) {
      this.registerer
        .unregister()
        .catch(() => {})
        .finally(() => {
          this.registerer = null;
        });
    } else {
      this.registerer = null;
    }
    if (this.userAgent) {
      this.userAgent
        .stop()
        .catch(() => {})
        .finally(() => {
          this.userAgent = null;
        });
    } else {
      this.userAgent = null;
    }
  }
}
